import { isUndefined, maxBy, partition } from 'lodash';
import { ActionsStack } from './actions-stack';
import { ActionType, ServerRequestsQueue } from './server-requests-queue';
import type { RootStore } from '~/stores/root.store';
import {
    FlowApplyActionResponseStatus,
    FlowApplyActionsErrors,
    IFlowApplyActionResponse
} from '~/types/server.types';
import {
    calculateMultiStageActionId,
    mapActionIdToAction,
    mapActionNumberToAction,
    MultiStageIdParts,
    validateCommand
} from './actions.utils';
import { Action, ActionStatus, ISingleAction } from './action';
import { FlowAction } from './flow-action';
import {
    IBaseFlowContext,
    RegainControlModalReasons
} from '~/types/builder.types';
import {
    IMultiStageAction,
    MultiStageFlowAction
} from './multi-stage-flow-action';
import { AnalyticsEvents } from '../analytics/analytics-events';
import { FlowActionGroup, IFlowActionGroup } from './FlowActionGroup';
import {
    ActionManagerLastActionNumberReason,
    ActionNumberRaiseReason,
    FetchFlowReasons,
    LogEvents
} from '../fullstory/log-events';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import { tryParseToJsonString } from '~/utils/json.utils';
import { trackError } from '~/services/logger/error.utils';
import i18next from 'i18next';
import { RecoveryMode } from '~/services/actions-manager/recovery-mode';
import { logError } from '~/services/logger/logger.utils';

type RedoUndoSourceAction = 'button' | 'keyboard' | 'lead_form_wizard';

interface IRedoUndoOptions {
    source?: RedoUndoSourceAction;
}

const DefaultRedoUndoOptions: IRedoUndoOptions = {
    source: null
};

interface IFailActionsOptions {
    isNetworkError: boolean;
    error: any;
}

export class ActionsManager {
    rootStore: RootStore;
    actionsStack: ActionsStack;
    serverRequestsQueue: ServerRequestsQueue;
    lastActionNumber: number;
    inProcessMultiStageActions: Record<string, MultiStageFlowAction>;
    currentActionGroup: FlowActionGroup;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.actionsStack = new ActionsStack(this.rootStore);
        this.serverRequestsQueue = new ServerRequestsQueue(this.rootStore, {
            recoveryMode: RecoveryMode.retryOnNetworkError,
            recoveryModeOptions: {
                incrementalDelay: {
                    startAfterRetries: 3,
                    backoff: 5000,
                    max: 30000
                }
            }
        });
        this.lastActionNumber = -1;
        this.inProcessMultiStageActions = {};
        this.currentActionGroup = null;

        this.onActionsFailed = this.onActionsFailed.bind(this);
        this.onOnlineStatusChanged = this.onOnlineStatusChanged.bind(this);
        this.onActionsSent = this.onActionsSent.bind(this);
        this.onNetworkConnectivityIssue =
            this.onNetworkConnectivityIssue.bind(this);

        this.serverRequestsQueue.onActionsFailed(this.onActionsFailed);
        this.serverRequestsQueue.onActionsSent(this.onActionsSent);
        this.serverRequestsQueue.onNetworkConnectivityIssue(
            this.onNetworkConnectivityIssue
        );
    }

    get isGroupingActions() {
        return !!this.currentActionGroup;
    }

    get isMultiStageInProgress() {
        return !!Object.keys(this.inProcessMultiStageActions).length;
    }

    get hasPendingChanges() {
        return this.serverRequestsQueue.hasPendingChanges;
    }

    get currentMultiStageAction() {
        const inProcessMultiStageActions = Object.values(
            this.inProcessMultiStageActions
        );

        return inProcessMultiStageActions.length === 1
            ? inProcessMultiStageActions[0]
            : null;
    }

    get requestsQueue() {
        return this.serverRequestsQueue;
    }

    createAction<TContext extends IBaseFlowContext>(
        actionDataCreator: (
            newAction: ISingleAction<TContext>
        ) => ISingleAction<TContext>
    ) {
        const newAction = new FlowAction<TContext>(this.rootStore); // TODO: Refactor in the future to create the type dynamically
        newAction.init(actionDataCreator(newAction));

        return newAction;
    }

    createMultiStageAction<TContext extends IBaseFlowContext>(
        actionDataCreator: (
            newAction: IMultiStageAction<TContext>
        ) => IMultiStageAction<TContext>
    ) {
        const { fullstoryService } = this.rootStore;

        this.finishInProgressMultiStageActions();

        const newAction = new MultiStageFlowAction<TContext>(this.rootStore); // TODO: Refactor in the future to create the type dynamically
        newAction.initMultiStage(actionDataCreator(newAction));

        fullstoryService.debug(
            LogEvents.StartComplexAction(newAction, 'multi stage')
        );

        return newAction;
    }

    groupActions(
        actionDataCreator: (newAction: IFlowActionGroup) => IFlowActionGroup
    ) {
        this.finishInProgressMultiStageActions();
        const newAction = new FlowActionGroup(this.rootStore); // TODO: Refactor in the future to create the type dynamically
        newAction.initActionGroup(actionDataCreator(newAction));

        if (this.isGroupingActions) {
            newAction.groupActions();
            this.currentActionGroup.addAction(newAction);

            // If we have an active group-actions action we don't want to start a new want but actually to continue grouping the actions in the outer group action.
            return;
        }

        const { fullstoryService } = this.rootStore;

        this.currentActionGroup = newAction;

        try {
            fullstoryService.debug(
                LogEvents.StartComplexAction(newAction, 'grouping actions')
            );

            this.currentActionGroup.groupActions();

            if (this.currentActionGroup.wasAppliedSideEffectOverride) {
                this.currentActionGroup.applySideEffects();
            }
        } catch (e) {
            const currentGroupAction = this.currentActionGroup;
            this.currentActionGroup = null;

            fullstoryService.error(e);
            currentGroupAction.revertCommands();

            throw e;
        }

        this.finishGroupingActions();
    }

    private finishGroupingActions() {
        const actionGroup = this.currentActionGroup;
        if (!actionGroup) {
            return;
        }
        const { fullstoryService } = this.rootStore;

        this.currentActionGroup = null;

        try {
            actionGroup.ctx.revertCommands = actionGroup.ctx.revertCommands
                .slice()
                .reverse();
            actionGroup.dataForServer.apply =
                actionGroup.createApplyDataForServer();
            this.actionsStack.push(actionGroup);
            this.setActionNumber(
                actionGroup,
                ActionNumberRaiseReason.groupAction
            );
            this.initActionApplyData(actionGroup);
            this.serverRequestsQueue.push(actionGroup, ActionType.apply);

            fullstoryService.debug(
                LogEvents.EndComplexAction(actionGroup, 'grouping actions')
            );
        } catch (e) {
            actionGroup.revertCommands();
            fullstoryService.error(
                LogEvents.FailedEndingComplexAction(
                    actionGroup,
                    'grouping actions'
                )
            );

            throw e;
        }
    }

    doNewApplyAction<TContext extends IBaseFlowContext>(
        action: Action<TContext>
    ) {
        const { actionLoggerService } = this.rootStore;

        this.finishInProgressMultiStageActions();

        action.createCommands();

        const isValid = action.ctx.commands.every(cmd =>
            validateCommand(cmd, this.rootStore)
        );

        if (!isValid) {
            return;
        }

        const actionLog = actionLoggerService.initActionLog();

        try {
            this.applyAction(action);
        } catch (error) {
            this.handleStoreActionApplicationFailure(error, action);

            if (this.isGroupingActions) {
                throw error; // We want in this case to also fail the containing group action run.
            }

            return;
        }

        if (!this.isGroupingActions) {
            this.setActionNumber(action, ActionNumberRaiseReason.normalAction);
        }

        actionLog?.addActionGeneralData(
            ActionType.apply,
            action,
            this.isGroupingActions ? 'Group' : null
        );
        actionLoggerService.logActionLog();

        if (this.isGroupingActions) {
            this.currentActionGroup.addAction(action);

            return;
        }

        this.actionsStack.push(action);

        this.serverRequestsQueue.push(action, ActionType.apply);
    }

    private handleStoreActionApplicationFailure(error: any, action: Action) {
        const { fullstoryService } = this.rootStore;

        fullstoryService.error(error);
        action.rollbackCommandsLocally();
        this.showFailedActionToast();
    }

    setActionNumber(action: Action, reason: ActionNumberRaiseReason) {
        const {
            fullstoryService,
            sessionStore: { tabChannel }
        } = this.rootStore;

        action.setActionNumber(++this.lastActionNumber);

        fullstoryService.info(LogEvents.SettingActionNumber(action, reason));

        if (tabChannel) {
            const {
                configStore: { sessionId },
                builderUiStore: { selectedFlowId }
            } = this.rootStore;
            tabChannel.postMessage(
                JSON.stringify({
                    type: 'update',
                    sessionId,
                    flowId: selectedFlowId
                })
            );
        }
    }

    initActionApplyData(action: Action) {
        action.appliedAt = new Date();
        action.setIsSentToServer(false);
    }

    applyStageForAction(action: MultiStageFlowAction, stage: string) {
        const { actionLoggerService } = this.rootStore;

        const actionLog = actionLoggerService.initActionLog();

        this.initActionApplyData(action);

        action.createStageCommands(stage);
        action.applyLastStageCommands();
        if (action.stages[stage].applySideEffects) {
            action.stages[stage].applySideEffects();
        }

        actionLog?.addActionGeneralData(ActionType.apply, action, 'Stage');
        actionLoggerService.logActionLog();

        if (action.status !== ActionStatus.appliedLocally) {
            action.setStatus(ActionStatus.appliedLocally);

            if (this.inProcessMultiStageActions[action.id]) {
                this.finishMultiStageAction(
                    this.inProcessMultiStageActions[action.id]
                );
            }

            this.inProcessMultiStageActions[action.id] = action;

            if (!this.isGroupingActions) {
                this.actionsStack.push(action);
            }
        }
    }

    finishMultiStageAction(action: MultiStageFlowAction) {
        const { fullstoryService } = this.rootStore;

        const inProgressAction = this.inProcessMultiStageActions[action.id];
        if (!inProgressAction || !isUndefined(action.action_number)) {
            return;
        }

        if (this.isGroupingActions) {
            this.currentActionGroup.addAction(action);
            delete this.inProcessMultiStageActions[action.id];
            return;
        }

        this.setActionNumber(action, ActionNumberRaiseReason.multiStageAction);

        if (!action.dataForServer.apply) {
            action.dataForServer.apply = action.createApplyDataForServer();
        }
        delete this.inProcessMultiStageActions[action.id];

        this.serverRequestsQueue.push(action, ActionType.apply);

        fullstoryService.debug(
            LogEvents.EndComplexAction(action, 'multi stage action')
        );
    }

    private applyAction(action: Action) {
        this.initActionApplyData(action);

        action.applyCommands();
        if (action.applySideEffects) {
            action.applySideEffects();
        }

        if (!action.dataForServer.apply) {
            action.dataForServer.apply = action.createApplyDataForServer();
        }
    }

    undoAction({ source = null }: IRedoUndoOptions = DefaultRedoUndoOptions) {
        const action = this.actionsStack.returnToLastSucceedAction();
        if (!action) {
            return null;
        }

        const { actionLoggerService } = this.rootStore;

        this.trackAction(AnalyticsEvents.UndoAction, source, action);

        const actionLog = actionLoggerService.initActionLog();
        this.initActionApplyData(action);

        // Ex. for not completed is multi stage that never were done.
        const isCompletedAction = !isUndefined(action.action_number);

        try {
            action.revertCommands();
            if (action.revertSideEffects) {
                action.revertSideEffects();
            }

            if (!action.dataForServer.revert) {
                action.dataForServer.revert =
                    action.createRevertDataForServer();
            }
        } catch (error) {
            this.handleStoreActionApplicationFailure(error, action);

            return null;
        }

        this.actionsStack.onAfterUndo();

        if (isCompletedAction) {
            this.setActionNumber(action, ActionNumberRaiseReason.undoAction);
        }

        actionLog?.addActionGeneralData(ActionType.undo, action);
        actionLoggerService.logActionLog();

        if (
            action instanceof MultiStageFlowAction &&
            this.inProcessMultiStageActions[(<MultiStageFlowAction>action).id]
        ) {
            this.actionsStack.removeAction(action);
            delete this.inProcessMultiStageActions[
                (<MultiStageFlowAction>action).id
            ];

            return null;
        }

        this.serverRequestsQueue.push(action, ActionType.undo);

        return action;
    }

    revertToActionNumber({
        actionNumber,
        source
    }: {
        actionNumber: number;
        source: RedoUndoSourceAction;
    }) {
        let numberOfActionsToRevert = this.lastActionNumber - actionNumber;
        while (numberOfActionsToRevert > 0) {
            this.undoAction({ source });
            numberOfActionsToRevert--;
        }
    }

    redoAction({ source = null }: IRedoUndoOptions = DefaultRedoUndoOptions) {
        const action = this.actionsStack.getNextAction();
        if (!action) {
            return null;
        }

        const { actionLoggerService } = this.rootStore;

        const actionLog = actionLoggerService.initActionLog();
        this.trackAction(AnalyticsEvents.RedoAction, source, action);

        try {
            this.applyAction(action);
        } catch (error) {
            this.handleStoreActionApplicationFailure(error, action);

            return null;
        }

        this.actionsStack.onAfterRedo();

        this.setActionNumber(action, ActionNumberRaiseReason.redoAction);

        actionLog?.addActionGeneralData(ActionType.redo, action);
        actionLoggerService.logActionLog();

        this.serverRequestsQueue.push(action, ActionType.redo);

        return action;
    }

    private trackAction(
        event: AnalyticsEvents,
        source: RedoUndoSourceAction,
        action: Action
    ) {
        const { analyticsService } = this.rootStore;

        analyticsService.track(event, {
            action: source === 'button' ? 'click' : undefined,
            source,
            actionName: action.name
        });
    }

    async onActionsFailed(actions, error) {
        const { flowCtrl, builderUiStore } = this.rootStore;

        const didFailAnyAction = this.handleActionsFailure(actions, error);

        if (didFailAnyAction) {
            this.showFailedActionToast();
            this.serverRequestsQueue.clearQueue();
        }

        await flowCtrl.fetchFlow(builderUiStore.selectedFlowId, {
            reason: FetchFlowReasons.actionFailedInServer,
            forceMerge: error?.data?.error_data?.force_merge
        }); // TODO: not called on second failure.
    }

    private showFailedActionToast() {
        const { toastStore } = this.rootStore;

        const t = i18next.getFixedT(null, 'builder');

        toastStore.add({
            type: ToastType.Error,
            text: t('errors.general_builder_error')
        });
    }

    onActionsSent(sentActionsClones: Array<Action>) {
        const sentActions = Object.values(
            mapActionIdToAction(
                sentActionsClones.map(({ _id }) => _id),
                this.actionsStack.stack,
                sentActionsClones
            )
        );

        sentActions.forEach(action => {
            action.setIsSentToServer(true);
        });
    }

    onNetworkConnectivityIssue({ isStable }: { isStable: boolean }) {
        const { networkStore } = this.rootStore;

        networkStore.setIsOnline(isStable);
    }

    async onOnlineStatusChanged(isOnline) {
        const { fullstoryService, flowCtrl, builderUiStore } = this.rootStore;

        if (!isOnline) {
            fullstoryService.warn(LogEvents.UserOffline);

            return -1;
        }

        fullstoryService.warn(LogEvents.UserBackOnline);

        const {
            lastActionNumberInServer,
            succeedSentAction,
            failedSentActions
        } = await this.getSentActionsStatusInServer();

        succeedSentAction.forEach(x => x.setToSuccess());

        // is sent actions were failed in the server
        if (failedSentActions.length) {
            return this.syncWithFlowInServer(lastActionNumberInServer);
        }

        let offlineCreatedActions = null;
        try {
            offlineCreatedActions =
                await this.serverRequestsQueue.flushRequests();
        } finally {
            await flowCtrl.fetchFlow(builderUiStore.selectedFlowId, {
                reason: FetchFlowReasons.userIsBackOnline
            });
        }

        return offlineCreatedActions?.length;
    }

    private async syncWithFlowInServer(lastActionNumberInServer: number) {
        const { flowCtrl, builderUiStore } = this.rootStore;

        const lastSucceedActionInServer = this.actionsStack.findActionByNumber(
            lastActionNumberInServer + 1
        );

        this.failStackFromActionToEnd(lastSucceedActionInServer, {
            isNetworkError: false,
            error: null
        });

        await flowCtrl.fetchFlow(builderUiStore.selectedFlowId, {
            reason: FetchFlowReasons.actionFailedInServer,
            forceMerge: true
        });

        this.actionsStack.moveToAction(lastActionNumberInServer);

        return Promise.resolve();
    }

    private async getSentActionsStatusInServer() {
        const { flowCtrl, builderUiStore } = this.rootStore;

        const lastActionNumberInServer = await flowCtrl.getFlowLastActionNumber(
            builderUiStore.selectedFlowId
        );
        const sentActions = this.actionsStack.getUnresolvedSentActions();
        const [succeedSentAction, failedSentActions] = partition(
            sentActions,
            x => x.action_number <= lastActionNumberInServer
        );

        return {
            lastActionNumberInServer,
            succeedSentAction,
            failedSentActions
        };
    }

    getActionByIdParam<TContext extends IBaseFlowContext>(
        idParams: MultiStageIdParts
    ) {
        return this.getActionById<TContext>(
            calculateMultiStageActionId(idParams)
        );
    }

    getActionById<TContext extends IBaseFlowContext>(
        id: string
    ): MultiStageFlowAction<TContext> {
        return <MultiStageFlowAction<TContext>>(
            this.inProcessMultiStageActions[id]
        );
    }

    clear() {
        this.actionsStack.clear();
        this.serverRequestsQueue.clear();
        this.serverRequestsQueue.onActionsFailed(this.onActionsFailed);
        this.serverRequestsQueue.onActionsSent(this.onActionsSent);
        this.clearRetryLoop();
        this.currentActionGroup = null;
        this.inProcessMultiStageActions = {};
    }

    resetRedoUndoStack() {
        this.actionsStack.clear();
    }

    private handleActionsFailure(actions: Array<Action>, error: any) {
        const {
            fullstoryService,
            builderUiStore,
            analyticsService,
            configStore: { config }
        } = this.rootStore;
        const errorType =
            error?.response?.data?.error_type || error?.data?.error_type;
        const errorData =
            error?.response?.data?.error_data || error?.data?.error_data;
        const errorMessage = error?.data?.error_message;

        const { json: errorDataJson } = tryParseToJsonString(errorData);

        fullstoryService.error(
            LogEvents.ActionsNumbers(
                LogEvents.ApplyActionErrorData(
                    errorType,
                    errorDataJson,
                    errorMessage
                ),
                actions
            )
        );

        this.sendErrorAnalytics(errorType, errorDataJson, errorMessage);

        // eslint-disable-next-line default-case
        switch (errorType) {
            case FlowApplyActionsErrors.actionResolveFailed: {
                return this.failActionsByResults(actions, error, errorData);
            }
            case FlowApplyActionsErrors.flowGotCorrupted: {
                fullstoryService.error(
                    LogEvents.ActionsNumbers(
                        'The actions got the flow corrupted:',
                        actions
                    )
                );

                analyticsService.track(AnalyticsEvents.flowCorruption, {
                    corruptionType: errorData?.new_issues_found,
                    flowId: builderUiStore.selectedFlowId
                });

                if (config.environment === 'production') {
                    fullstoryService.trackFullstoryEvent({
                        event: 'error: corruption was found',
                        props: {
                            corruptionType: errorData?.new_issues_found
                        }
                    });
                    logError('corruption was found', {
                        corruptionType: errorData?.new_issues_found
                    });
                }

                return this.failActionsByResults(actions, error, errorData);
            }
            case FlowApplyActionsErrors.flowNotSyncedWithoutWebSockets:
            case FlowApplyActionsErrors.flowNotSynced: {
                fullstoryService.error(errorMessage);

                break;
            }
            case FlowApplyActionsErrors.flowNotInControl: {
                builderUiStore.setRegainControlModalOpenReason(
                    RegainControlModalReasons.flowNotInControl
                );

                break;
            }
            case FlowApplyActionsErrors.tempAccessTriedGetControlFromVendor: {
                builderUiStore.setRegainControlModalOpenReason(
                    RegainControlModalReasons.tempAccessTriedGetControlFromVendor
                );

                break;
            }
        }

        if (actions.length) {
            return this.failAllActions(actions[0], {
                isNetworkError: !error,
                error
            });
        }

        return false;
    }

    private sendErrorAnalytics(
        errorType: string,
        errorData: string,
        errorMessage: string
    ) {
        const {
            fullstoryService,
            analyticsService,
            configStore: { config }
        } = this.rootStore;

        try {
            const analyticsContext = {
                error: {
                    errorType,
                    errorData,
                    errorMessage
                }
            };
            analyticsService.track(AnalyticsEvents.flowError, analyticsContext);
            trackError({
                error: new Error(AnalyticsEvents.flowError),
                context: analyticsContext
            });
            if (config.environment === 'production') {
                fullstoryService.trackFullstoryEvent({
                    event: AnalyticsEvents.flowError,
                    props: analyticsContext
                });
            }
        } catch (analyticsReportingError) {
            fullstoryService.error(analyticsReportingError);
        }
    }

    private failActionsByResults(
        sentActions: Array<Action>,
        error,
        actionsResults: IFlowApplyActionResponse
    ) {
        if (!sentActions?.length) {
            return false;
        }

        const {
            builderUiStore: { selectedFlow },
            fullstoryService
        } = this.rootStore;

        const actionsIds = sentActions.map(x => x._id);
        const actionsNumbers = sentActions.map(x => x.action_number);

        const actionIdToActionInStack = mapActionIdToAction(
            actionsIds,
            this.actionsStack.stack,
            sentActions
        );
        const actionNumberToSentAction = mapActionNumberToAction(
            actionsNumbers,
            sentActions,
            sentActions
        );

        let didFailAnyAction = false;
        let lastSuccessActionNumber = selectedFlow.last_action_number;
        const actionsToRollback: Array<Action> = [];
        actionsResults.actions.forEach(actionResult => {
            const sentAction =
                actionNumberToSentAction[actionResult.action_number];
            const actionInStack = actionIdToActionInStack[actionResult._id];

            if (actionResult.status !== FlowApplyActionResponseStatus.success) {
                actionInStack.setToFailed();
                actionInStack.actionFailedInServer({ error, actionResult });

                // This only affect the action in the stack, we want to do it in order to rollback correctly
                // by the action status which persist not like the action in the stack the changes with
                // the user undo and redo actions.
                sentAction.setToFailed();
                actionsToRollback.push(sentAction);

                didFailAnyAction = true;
            } else {
                actionInStack.setToSuccess();
                actionInStack.onActionToServerResolved(actionResult, {});
                lastSuccessActionNumber = actionInStack.action_number;
            }
        });

        // This would happen on certain corruptions that we don't fail the actions on.
        if (!didFailAnyAction) {
            fullstoryService.warn(
                LogEvents.ActionStatusResultLog(
                    'Apply actions returned an error but all action succeed:',
                    actionsResults.actions
                )
            );

            return didFailAnyAction;
        }

        fullstoryService.error(
            LogEvents.ActionStatusResultLog(
                'Actions Status from server:',
                actionsResults.actions
            )
        );

        this.setLastActionNumber(
            lastSuccessActionNumber,
            ActionManagerLastActionNumberReason.actionFailure
        );

        const lastSentAction = maxBy(
            Object.values(actionNumberToSentAction),
            x => x.action_number
        );
        const actionToFailFrom = this.actionsStack.findActionByNumber(
            lastSentAction.action_number + 1
        );

        if (actionToFailFrom) {
            this.failStackFromActionToEnd(actionToFailFrom, {
                isNetworkError: false,
                error
            });
        }

        actionsToRollback
            .reverse()
            .forEach(actionToRollback =>
                actionToRollback.rollbackCommandsLocally()
            );

        return didFailAnyAction;
    }

    private failAllActions(
        action: Action,
        { isNetworkError, error }: IFailActionsOptions
    ) {
        const { lastSucceedActionNumber, didFailAnyActions } =
            this.failStackFromActionToEnd(action, {
                isNetworkError,
                error
            });

        if (didFailAnyActions) {
            this.setLastActionNumber(
                lastSucceedActionNumber,
                ActionManagerLastActionNumberReason.actionFailure
            );
        }

        return didFailAnyActions;
    }

    setLastActionNumber(
        lastSuccessActionNumber: number,
        reason: ActionManagerLastActionNumberReason
    ) {
        const {
            builderUiStore: { selectedFlow },
            fullstoryService
        } = this.rootStore;

        selectedFlow.setLastActionNumber(lastSuccessActionNumber);
        this.lastActionNumber = lastSuccessActionNumber;
        this.actionsStack.moveToAction(this.lastActionNumber);

        fullstoryService.info(
            LogEvents.SetLastActionNumber(lastSuccessActionNumber, reason)
        );
    }

    private failStackFromActionToEnd(
        action: Action,
        { isNetworkError, error }: IFailActionsOptions
    ) {
        const {
            fullstoryService,
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const { stack } = this.actionsStack;
        let failFromIndex = stack.findIndex(({ _id }) => _id === action._id);

        fullstoryService.error(
            LogEvents.FailRestOfActionStack(stack, failFromIndex)
        );

        if (action.isFailedStatus) {
            failFromIndex++;
        }

        let didFailAnyActions = false;
        let lastSucceedActionNumber = selectedFlow.last_action_number;
        const actionsToRollback = [];
        for (let index = failFromIndex; index < stack.length; index++) {
            const actionToFail = stack[index];
            // If there was a network error we want to be optimistic and believe that all the sent actions succeed because we are missing the result from the server
            // if they weren't, on the next request we will receive an error.
            if (actionToFail.isSentToServer && isNetworkError) {
                lastSucceedActionNumber = actionToFail.action_number;
                actionToFail.setToSuccess();
            } else {
                actionToFail.setToFailed();
                actionToFail.actionFailedInServer({ error });

                actionsToRollback.push(actionToFail);
                didFailAnyActions = true;
            }
        }

        actionsToRollback
            .reverse()
            .forEach(actionToRollback =>
                actionToRollback.rollbackCommandsLocally()
            );

        return { lastSucceedActionNumber, didFailAnyActions };
    }

    finishInProgressMultiStageActions() {
        const wipActions = Object.values(this.inProcessMultiStageActions);
        if (!wipActions.length) {
            return;
        }

        wipActions.forEach(action => this.finishMultiStageAction(action));
    }

    async handleConflict(conflictedActionNumber: number) {
        const { flowCtrl, builderUiStore } = this.rootStore;

        this.actionsStack.removeActionFromNumber(conflictedActionNumber);
        this.serverRequestsQueue.clear();

        // If currently there is a request that updates the server we want to synced with the server so we call fetch again.
        if (!this.serverRequestsQueue.isRequestInProgress) {
            return;
        }

        await flowCtrl.fetchFlow(builderUiStore.selectedFlowId, {
            reason: FetchFlowReasons.conflictWhenActionsBeingResolved
        });
    }

    clearRetryLoop() {
        this.serverRequestsQueue.clearRetryLoop();
    }

    findActionByNumber(actionNumber: number): Action {
        return this.actionsStack.findActionByNumber(actionNumber);
    }
}
